import { z } from "zod";
import { type ObligationBasis, type ObligationKind } from "./library.js";
import { nlsfbNode } from "./nlsfb.js";
import { parseFloorName } from "./parseFloorName.js";

/**
 * Import uit een Assetlog-export (knowledge/Assetlog-import.md). Twee formaten:
 * - `labels`: tabblad *Labels*, één rij per asset;
 * - `tasks`: tabblad *Tasks+asset*, één rij per taak met de assetvelden erbij (voorvoegsel `(Asset)`).
 * De server leest de Excel; hier staan het herkennen, de voorstellen en de vertalingen, zodat web en
 * server hetzelfde rekenen.
 */

export const ASSETLOG_SOURCE = "assetlog";
export const ASSETLOG_MAX_ROWS = 5000;
export const ASSETLOG_FORMATS = ["labels", "tasks"] as const;
export type AssetlogFormat = (typeof ASSETLOG_FORMATS)[number];
export const ASSETLOG_MODES = ["assets", "assets_tasks"] as const;
export type AssetlogMode = (typeof ASSETLOG_MODES)[number];

export type AssetlogCell = string | number | null;

export interface AssetlogAsset {
  /** Rijnummer in Excel (eerste rij met dit asset). */
  row: number;
  code: string;
  qr: string | null;
  product: string;
  reference: string | null;
  planonCode: string | null;
  building: string;
  floor: string;
  room: string;
  placement: string | null;
  components: string | null;
  remarks: string | null;
  brand: string | null;
  model: string | null;
  serialNumber: string | null;
  capacity: string | null;
  yearBuilt: number | null;
  warrantyUntil: string | null;
}

export interface AssetlogTask {
  row: number;
  taskId: string;
  assetCode: string;
  name: string;
  description: string | null;
  performer: string | null;
  unitPrice: number | null;
  amount: number | null;
  totalPrice: number | null;
  due: string | null;
  period: "annually" | "monthly" | "weekly" | null;
  periods: number | null;
  required: string | null;
}

export interface AssetlogParsed {
  format: AssetlogFormat;
  assets: AssetlogAsset[];
  tasks: AssetlogTask[];
  /** Rijen die niet gelezen kunnen worden (zonder code e.d.). */
  problems: { row: number; message: string }[];
}

const clean = (v: AssetlogCell | undefined): string | null => {
  if (v == null) return null;
  const s = String(v).replace(/\s+/g, " ").trim();
  return s && s !== "-" ? s : null;
};
const num = (v: AssetlogCell | undefined): number | null => {
  if (v == null || v === "") return null;
  const n = typeof v === "number" ? v : Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};
/** "2024-02-01 01:00:00", "2024-02-01T…" of een Excel-datum (al omgezet door de server). */
const isoDate = (v: AssetlogCell | undefined): string | null => {
  const s = clean(v);
  const m = s && /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
};

/** Kolomnaam zonder het voorvoegsel `(Asset)` en zonder hoofdletters/spaties. */
const headKey = (h: string) =>
  h
    .replace(/^\(asset\)/i, "")
    .replace(/\s+/g, "")
    .toLowerCase();

const LABEL_COLUMNS = ["qr", "code", "product", "building", "floor", "room"];

/** Welk Assetlog-formaat is dit (op de kopregel)? null = geen Assetlog-export. */
export function assetlogFormat(header: readonly (string | null)[]): AssetlogFormat | null {
  const raw = header.map((h) => (h ?? "").trim());
  const keys = new Set(raw.map(headKey));
  if (!LABEL_COLUMNS.every((c) => keys.has(c))) return null;
  return raw.some((h) => /^taskid$/i.test(h)) ? "tasks" : "labels";
}

export const ASSETLOG_FORMAT_ERROR =
  "Dit is geen Assetlog-export. Gebruik de Excel-export van Assetlog (tabblad Labels of Tasks+asset) met de kolommen QR, Code, Product, Building, Floor en Room.";

/** Rijen van een Assetlog-tabblad lezen (eerste rij = kopregel). */
export function parseAssetlog(header: readonly (string | null)[], rows: readonly AssetlogCell[][]): AssetlogParsed {
  const format = assetlogFormat(header);
  if (!format) throw new Error(ASSETLOG_FORMAT_ERROR);
  const raw = header.map((h) => (h ?? "").trim());
  // Asset-kolommen: bij `tasks` de (Asset)-variant, bij `labels` de gewone naam.
  const assetCol = (name: string) =>
    raw.findIndex((h) =>
      format === "tasks" ? h.toLowerCase() === `(asset)${name.toLowerCase()}` : headKey(h) === name.toLowerCase(),
    );
  const taskCol = (name: string) => raw.findIndex((h) => h.toLowerCase() === name.toLowerCase());
  const at = (r: readonly AssetlogCell[], i: number) => (i >= 0 ? r[i] : null);
  const A = Object.fromEntries(
    [
      "QR",
      "Code",
      "Product",
      "Reference",
      "PlanonCode",
      "Building",
      "Floor",
      "Room",
      "Placement",
      "Components",
      "Remarks",
      "Brand",
      "Model",
      "SerialNumber",
      "Capacity",
      "CapacityUnit",
      "BuiltIn",
      "EndOfWarranty",
    ].map((n) => [n, assetCol(n)]),
  ) as Record<string, number>;
  const T = Object.fromEntries(
    [
      "AssetCode",
      "TaskID",
      "TaskName",
      "TaskDescription",
      "Assignee",
      "UnitPriceNorm",
      "Amount",
      "TotalPrice",
      "Due",
      "PeriodType",
      "RepeatNumberOfPeriods",
      "Required",
    ].map((n) => [n, taskCol(n)]),
  ) as Record<string, number>;

  const assets = new Map<string, AssetlogAsset>();
  const tasks: AssetlogTask[] = [];
  const problems: AssetlogParsed["problems"] = [];
  rows.forEach((r, i) => {
    const row = i + 2;
    if (!r.some((c) => clean(c))) return;
    const code = clean(at(r, A.Code!)) ?? clean(at(r, T.AssetCode!));
    if (!code) return void problems.push({ row, message: "Geen Assetlog-code" });
    if (!assets.has(code)) {
      const cap = num(at(r, A.Capacity!));
      const built = num(at(r, A.BuiltIn!));
      assets.set(code, {
        row,
        code,
        qr: clean(at(r, A.QR!)),
        product: clean(at(r, A.Product!)) ?? "Asset",
        reference: clean(at(r, A.Reference!)),
        planonCode: clean(at(r, A.PlanonCode!)),
        building: clean(at(r, A.Building!)) ?? "",
        floor: clean(at(r, A.Floor!)) ?? "",
        room: clean(at(r, A.Room!)) ?? "",
        placement: clean(at(r, A.Placement!)),
        components: clean(at(r, A.Components!)),
        remarks: clean(at(r, A.Remarks!)),
        brand: clean(at(r, A.Brand!)),
        model: clean(at(r, A.Model!)),
        serialNumber: clean(at(r, A.SerialNumber!)),
        capacity: cap ? [String(cap).replace(".", ","), clean(at(r, A.CapacityUnit!))].filter(Boolean).join(" ") : null,
        yearBuilt: built && built >= 1800 && built <= 2100 ? Math.trunc(built) : null,
        warrantyUntil: isoDate(at(r, A.EndOfWarranty!)),
      });
    }
    if (format !== "tasks") return;
    const taskId = clean(at(r, T.TaskID!));
    const name = clean(at(r, T.TaskName!));
    if (!taskId || !name) return void problems.push({ row, message: "Taak zonder TaskID of naam" });
    const period = clean(at(r, T.PeriodType!))?.toLowerCase();
    tasks.push({
      row,
      taskId,
      assetCode: code,
      name,
      description: clean(at(r, T.TaskDescription!)),
      performer: cleanPerformer(at(r, T.Assignee!)),
      unitPrice: num(at(r, T.UnitPriceNorm!)),
      amount: num(at(r, T.Amount!)),
      totalPrice: num(at(r, T.TotalPrice!)),
      due: isoDate(at(r, T.Due!)),
      period: period === "annually" || period === "monthly" || period === "weekly" ? period : null,
      periods: num(at(r, T.RepeatNumberOfPeriods!)),
      required: clean(at(r, T.Required!))?.toLowerCase() ?? null,
    });
  });
  return { format, assets: [...assets.values()], tasks, problems };
}

/** Uitvoerder uit `Assignee`: "-" en "0" zijn leeg, spaties in een e-mailadres weg. */
function cleanPerformer(v: AssetlogCell | undefined): string | null {
  const s = clean(v);
  if (!s || s === "0") return null;
  return s.includes("@") ? s.replace(/\s+/g, "").toLowerCase() : s;
}

// ── assetvelden ──

/** "Transportband – Horizontaal 3"; max. 120 tekens. */
export function assetlogName(a: Pick<AssetlogAsset, "product" | "reference">): string {
  const n = a.reference && a.reference !== a.product ? `${a.product} – ${a.reference}` : a.product;
  return n.length > 120 ? `${n.slice(0, 119).trimEnd()}…` : n;
}

/** Notities: plek, componenten, opmerkingen en de Assetlog-ruimte als die geen ruimte wordt. */
export function assetlogNotes(a: AssetlogAsset, unusedRoom: string | null): string | null {
  const lines = [
    unusedRoom && `Plek: ${unusedRoom}`,
    a.placement && `Locatie: ${a.placement}`,
    a.components && `Componenten: ${a.components}`,
    a.remarks && `Opmerkingen: ${a.remarks}`,
  ].filter(Boolean);
  return lines.length ? lines.join("\n") : null;
}

// ── locaties ──

/** Vergelijkingsvorm van een naam: kleine letters, geen leestekens of "algemeen". */
export const normName = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/\[general\]|\balgemeen\b|\bgeneral\b/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

/** "algemeen", "building", "basement_[general]": geen eigen plek. */
const isGeneral = (s: string) => {
  const n = normName(s.replace(/_/g, " "));
  return !n || /\[general\]/i.test(s) || /^(building|basement|\d+(st|nd|rd|th) floor|floor|gebouw)$/.test(n);
};

export const locationKey = (a: Pick<AssetlogAsset, "building" | "floor" | "room">) =>
  [a.building, a.floor, a.room].map(normName).join("/");

export type FloorChoice = { kind: "existing"; id: string } | { kind: "new"; name: string } | { kind: "none" };
export type RoomChoice = { kind: "existing"; id: string } | { kind: "new"; name: string } | { kind: "none" };
export type LocationSource = "remembered" | "found" | "new";

export interface LocationProposal {
  floor: FloorChoice;
  room: RoomChoice;
  source: LocationSource;
}

export interface PlanFloor {
  id: string;
  name: string;
  level: string;
}
export interface PlanRoom {
  id: string;
  floorId: string;
  name: string;
}

const LEVEL = /^(-?\d{1,2})\s+(.+)$/;

/**
 * Wat wordt verdieping en ruimte? Eén Assetlog-gebouw (het gebouw zelf): Floor → verdieping, Room →
 * ruimte. Meerdere Assetlog-gebouwen (hallen, vleugels): Building → verdieping, Floor → ruimte;
 * begint Floor met een nummer, dan "<Building> <nummer>" en Room als ruimte.
 */
export function assetlogPlace(
  a: Pick<AssetlogAsset, "building" | "floor" | "room">,
  multiBuilding: boolean,
): { floor: string | null; floorLevel: string | null; room: string | null } {
  const floor = isGeneral(a.floor) ? "" : a.floor;
  const room = isGeneral(a.room) ? "" : a.room;
  if (!multiBuilding) {
    if (!floor) return { floor: null, floorLevel: null, room: null };
    const p = parseFloorName(floor);
    return LEVEL.test(floor) || p.level
      ? { floor: p.name, floorLevel: p.level, room: room || null }
      : { floor, floorLevel: null, room: room || null };
  }
  const building = isGeneral(a.building) ? "" : a.building;
  if (!building) return { floor: null, floorLevel: null, room: null };
  const m = LEVEL.exec(floor);
  if (m) return { floor: `${building} ${m[1]}`, floorLevel: null, room: room || null };
  return { floor: building, floorLevel: null, room: floor || room || null };
}

const sameName = (a: string, b: string) => {
  const x = normName(a);
  const y = normName(b);
  return !!x && !!y && (x === y || x.startsWith(`${y} `) || y.startsWith(`${x} `));
};

/** Voorstel per locatie: eerder gekoppeld > gevonden op naam > nieuw. */
export function proposeLocation(
  place: ReturnType<typeof assetlogPlace>,
  ctx: { floors: PlanFloor[]; rooms: PlanRoom[]; remembered?: { floorId: string; roomId: string | null } | null },
): LocationProposal {
  const r = ctx.remembered;
  if (r && ctx.floors.some((f) => f.id === r.floorId) && (!r.roomId || ctx.rooms.some((x) => x.id === r.roomId)))
    return {
      floor: { kind: "existing", id: r.floorId },
      room: r.roomId ? { kind: "existing", id: r.roomId } : { kind: "none" },
      source: "remembered",
    };
  if (!place.floor) return { floor: { kind: "none" }, room: { kind: "none" }, source: "new" };
  const f =
    ctx.floors.find((x) => normName(x.name) === normName(place.floor!)) ??
    (place.floorLevel ? ctx.floors.find((x) => x.level && x.level === place.floorLevel) : undefined) ??
    ctx.floors.find((x) => sameName(x.name, place.floor!));
  if (!f)
    return {
      floor: { kind: "new", name: place.floor },
      room: place.room ? { kind: "new", name: place.room } : { kind: "none" },
      source: "new",
    };
  if (!place.room) return { floor: { kind: "existing", id: f.id }, room: { kind: "none" }, source: "found" };
  const onFloor = ctx.rooms.filter((x) => x.floorId === f.id);
  const room =
    onFloor.find((x) => normName(x.name) === normName(place.room!)) ??
    onFloor.find((x) => sameName(x.name, place.room!));
  return {
    floor: { kind: "existing", id: f.id },
    room: room ? { kind: "existing", id: room.id } : { kind: "new", name: place.room },
    source: room ? "found" : "new",
  };
}

// ── indeling (NL-SfB → NLBE-SfB) ──

/**
 * Vertaling per Assetlog-product (genormaliseerde naam, of het begin ervan). De subcodes van NL-SfB
 * en NLBE-SfB verschillen, dus nooit cijfer voor cijfer.
 */
const PRODUCT_MAP: [RegExp, string][] = [
  [/^overheaddeur/, "31.62"],
  [/^loading dock/, "31.6"],
  [/^snelloopdeur/, "32.6"],
  [/^(schuifdeur|buitendraaideur|draaideur)/, "31.5"],
  [/^warmtepomp/, "56.21"],
  [/^(split systeem|airco)/, "56.1"],
  [/^persluchtcompressor/, "54.21"],
  [/^centrifugaal ventilator/, "57.2"],
  [/^(krachtstroominstallatie|ls verdeelinrichting)/, "61.5"],
  [/^reclameverlichting/, "63.3"],
  [/^verlichting/, "63.2"],
  [/^(brandblusinstallatie|handblusser|brandslanghaspel|brandwerende doorvoer)/, "55"],
  [/^brandmeld/, "65.1"],
  [/^inbraaksignalering/, "65.2"],
  [/^miva noodsignaal/, "65.4"],
  [/^(transportband|transportinstallatie)/, "66.34"],
  [/^transportlift/, "66.31"],
  [/^personen ?lift/, "66.1"],
  [/^(heftruck|elektrische pompwagen|pompwagen|stapelaar)/, "66.37"],
  [/^verrijdbare stelling/, "86.1"],
  [/^stelling/, "76.1"],
  [/^opslagtank/, "76.2"],
  [/^drinkwaterinstallatie/, "53.1"],
  [/^voorraadboiler/, "53.2"],
  [/^vetvanginstallatie/, "52.3"],
  [/^(grootkeukeninstallatie|koelcel|vriescel|koel vriescel)/, "73.1"],
  [/^(koelkast|vrieskast|oven|kookplaat|magnetron) (inbouw|elektrisch)/, "73.2"],
  [/^(koelkast|vrieskast|oven|magnetron) losstaand/, "83.1"],
  [/^(koffieautomaat|waterkoker|verkoopmachine)/, "83.1"],
  [
    /^(wasmachine|wasdroger|stofzuiger|industriele stofzuiger|hogedrukreiniger|schrobmachine|industriele schrobmachine)/,
    "85",
  ],
  [/^(server|printer)/, "87.2"],
  [/^industriele mixer/, "87.1"],
  [/^locker/, "86.1"],
  [/^(tempereer|ontvorm|vormvul|doseermachine|overtrek|bindler|koelbaan|robotarm|pindarots)/, "77.2"],
  [
    /^(inpak|label|codeer|weeg|doseer|metaaldetect|wikkel|dozensluit|heat ?seal|draaitafel|kantelaar|granulaatstrooier)/,
    "87.1",
  ],
];

/**
 * Elementen (2 cijfers) die in NL-SfB en NLBE-SfB hetzelfde betekenen; terugval als het product niet
 * in de tabel staat. 55 is koeling in NL-SfB (NLBE: 56).
 */
const SAME_ELEMENT: Record<string, string> = {
  ...Object.fromEntries(
    ["21", "22", "23", "24", "27", "28", "31", "32", "33", "34", "37", "41", "42", "43", "44", "45", "47"].map((c) => [
      c,
      c,
    ]),
  ),
  ...Object.fromEntries(
    [
      "52",
      "53",
      "54",
      "56",
      "57",
      "61",
      "62",
      "63",
      "64",
      "65",
      "66",
      "73",
      "74",
      "75",
      "76",
      "82",
      "83",
      "85",
      "86",
    ].map((c) => [c, c]),
  ),
  "55": "56",
};

export const productKey = (product: string) => normName(product);

/** "algemeen" en lege producten: voorstel overslaan. */
export const isSkippedProduct = (product: string) => !normName(product) || normName(product) === "asset";

export type ElementSource = "remembered" | "table" | "element" | "none";

/** Voorstel NLBE-SfB-code voor een Assetlog-product; zonder volledige tabel op 2 cijfers. */
export function assetlogElement(
  product: string,
  planonCode: string | null,
  full: boolean,
): { code: string | null; source: ElementSource } {
  const key = productKey(product);
  const hit = PRODUCT_MAP.find(([re]) => re.test(key))?.[1];
  // Verzamelcodes (bijv. 8410.100.xx) alleen via de producttabel.
  const el = planonCode && !/^84/.test(planonCode) ? SAME_ELEMENT[planonCode.slice(0, 2)] : undefined;
  const code = hit ?? el ?? null;
  if (!code) return { code: null, source: "none" };
  return { code: fitElement(code, full), source: hit ? "table" : "element" };
}

/** Code inkorten tot 2 cijfers als de organisatie alleen elementen gebruikt. */
export function fitElement(code: string, full: boolean): string | null {
  const c = full ? code : code.slice(0, 2);
  return nlsfbNode(c) ? c : null;
}

// ── taken (keuringen en vervangingen) ──

export type AssetlogTaskType = "replacement" | "obligation";

export function assetlogTaskType(name: string): AssetlogTaskType {
  return /^vervang/.test(normName(name)) ? "replacement" : "obligation";
}

/** Interval van een taak; null = eenmalig of onbekend. */
export function assetlogInterval(t: Pick<AssetlogTask, "period" | "periods">): {
  months: number | null;
  weeks: number | null;
} {
  const n = t.periods && t.periods > 0 ? Math.round(t.periods) : null;
  if (!n || !t.period) return { months: null, weeks: null };
  if (t.period === "weekly") return { months: null, weeks: n };
  return { months: t.period === "annually" ? n * 12 : n, weeks: null };
}

export function assetlogKind(name: string): ObligationKind {
  const n = normName(name);
  if (/keuring|nen ?3140|lekcontrole/.test(n)) return "keuring";
  if (/inspectie/.test(n)) return "inspectie";
  if (/controle|spoel/.test(n)) return "controle";
  if (/onderhoud|revisie|herstel|afpersen/.test(n)) return "onderhoud";
  return "overig";
}

export function assetlogBasis(required: string | null): ObligationBasis {
  return required === "legally_required" ? "wettelijk" : "eigen";
}

/** Bedrag in centen: totaalprijs, anders eenheidsprijs × aantal. 0 = geen bedrag. */
export function assetlogCents(t: Pick<AssetlogTask, "totalPrice" | "unitPrice" | "amount">): number | null {
  const v = t.totalPrice || (t.unitPrice ? t.unitPrice * (t.amount || 1) : 0);
  return v && v > 0 ? Math.round(v * 100) : null;
}

// ── API ──

const fileB64 = z.string().min(1).max(14_000_000);
const floorChoice = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("existing"), id: z.uuid() }),
  z.object({ kind: z.literal("new"), name: z.string().trim().min(1).max(100) }),
  z.object({ kind: z.literal("none") }),
]);
const roomChoice = floorChoice;

export const assetlogPlanBodySchema = z.object({ file: fileB64 });

export const assetlogImportBodySchema = z.object({
  file: fileB64,
  mode: z.enum(ASSETLOG_MODES).default("assets"),
  existing: z.enum(["update", "skip"]).default("update"),
  locations: z
    .array(z.object({ key: z.string().max(400), floor: floorChoice, room: roomChoice }))
    .max(ASSETLOG_MAX_ROWS)
    .default([]),
  products: z
    .array(
      z.object({ key: z.string().max(200), code: z.string().max(12).nullable(), skip: z.boolean().default(false) }),
    )
    .max(ASSETLOG_MAX_ROWS)
    .default([]),
});
export type AssetlogImportBody = z.infer<typeof assetlogImportBodySchema>;

export type AssetlogRowStatus = "new" | "update" | "skip" | "error";

export interface AssetlogPlanResponse {
  format: AssetlogFormat;
  fileRows: number;
  locations: {
    key: string;
    building: string;
    floor: string;
    room: string;
    assets: number;
    proposal: LocationProposal;
  }[];
  products: {
    key: string;
    product: string;
    planonCode: string | null;
    assets: number;
    code: string | null;
    source: ElementSource;
    skip: boolean;
  }[];
  rows: {
    row: number;
    code: string;
    name: string;
    productKey: string;
    status: AssetlogRowStatus;
    /** Status zonder "overslaan" per product (dat kan de gebruiker nog wijzigen). */
    baseStatus: AssetlogRowStatus;
    message: string | null;
  }[];
  tasks: {
    obligations: number;
    replacements: number;
    /** Vervangingen die vervallen omdat een asset er maar één heeft (de duurste blijft). */
    mergedReplacements: number;
    withoutInterval: number;
    withoutDate: number;
  } | null;
  problems: { row: number; message: string }[];
}

export interface AssetlogImportResult {
  assets: { created: number; updated: number; skipped: number; errors: number };
  floorsCreated: number;
  roomsCreated: number;
  obligations: { created: number; updated: number };
  replacements: { created: number; updated: number; skipped: number };
  /** Assets van deze import zonder plek op de tekening. */
  unplaced: number;
  errors: { row: number; code: string; message: string }[];
}
